import { useState, useCallback, useContext } from 'react';
import {
  Box, Container, Grid, Typography, Button, Select, MenuItem, FormControl,
  InputLabel, Card, CardContent, Chip, CircularProgress, Alert, Paper, Divider,
} from '@mui/material';
import {
  CloudUpload, CheckCircle, Cancel, VerifiedUser, Search, Image as ImageIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { GlobalContext } from '../../components/GlobalStateProvide/GlobalStateProvider';
import BackBtn from '../../components/backButton/BackBtn';
import { postRequest } from '../../utils/ApiRequests';
import {
  extractAllFromImage, DOCUMENT_TYPES,
} from '../../utils/ocrService';
import './kycVerification.scss';

const KycDocumentVerification = () => {
  const { handleSnackBar } = useContext(GlobalContext);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [decentroResult, setDecentroResult] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const f = acceptedFiles[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setDecentroResult(null);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const handleRunOcr = async () => {
    if (!file || !selectedDocType) {
      handleSnackBar(true, 'Please select document type and upload an image', 'warning');
      return;
    }
    setLoading(true);
    setResult(null);
    setDecentroResult(null);
    try {
      const data = await extractAllFromImage(file, selectedDocType);
      setResult(data);
      handleSnackBar(true, 'OCR completed successfully', 'success');
    } catch (err) {
      console.error('OCR Error:', err);
      handleSnackBar(true, 'OCR processing failed: ' + (err.message || 'unknown error. Check browser console (F12) for details.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyWithDecentro = async () => {
    if (!result || !result.docNumber) {
      handleSnackBar(true, 'No document number extracted to verify', 'warning');
      return;
    }
    setVerifying(true);
    setDecentroResult(null);
    try {
      const payload = {
        verificationId: result.docNumber,
        verificationIdType: selectedDocType,
      };
      const res = await postRequest('admin/document-verification', payload);
      setDecentroResult({ success: true, data: res.data });
      handleSnackBar(true, 'Document verified successfully', 'success');
    } catch (err) {
      const msg = err?.response?.data?.msg || err.message;
      setDecentroResult({ success: false, error: msg });
      handleSnackBar(true, msg, 'error');
    } finally {
      setVerifying(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setDecentroResult(null);
    setSelectedDocType('');
  };

  return (
    <Box className="kyc-verification-page">
      <Container maxWidth="lg">
        <Box className="page-header">
          <BackBtn />
          <Typography variant="h4" className="page-title">KYC Document Verification</Typography>
        </Box>

        <Paper className="kyc-section">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={selectedDocType}
                  label="Document Type"
                  onChange={(e) => { setSelectedDocType(e.target.value); setResult(null); setDecentroResult(null); }}
                >
                  {DOCUMENT_TYPES.map((dt) => (
                    <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box className="dropzone-wrapper" mt={3}>
            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              {preview ? (
                <img src={preview} alt="Document preview" className="preview-image" />
              ) : (
                <Box className="dropzone-placeholder">
                  <CloudUpload className="dropzone-icon" />
                  <Typography>Drag & drop document image here, or click to browse</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Supported: PNG, JPG, JPEG, WEBP (max 10MB)
                  </Typography>
                </Box>
              )}
            </div>
          </Box>

          {preview && (
            <Box className="action-bar" mt={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Search />}
                onClick={handleRunOcr}
                disabled={loading || !selectedDocType}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Run OCR'}
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleClear}>
                Clear
              </Button>
            </Box>
          )}
        </Paper>

        {result && (
          <Paper className="kyc-section result-section">
            <Typography variant="h6" className="section-title">
              <VerifiedUser className="section-icon" />
              OCR Extraction Results
            </Typography>
            <Divider />
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" className={`result-card ${result.isValid ? 'valid' : 'invalid'}`}>
                  <CardContent>
                    <Box className="result-header">
                      <Typography variant="subtitle2" color="textSecondary">Document Number</Typography>
                      <Chip
                        size="small"
                        icon={result.isValid ? <CheckCircle /> : <Cancel />}
                        label={result.isValid ? 'Valid Format' : 'Invalid Format'}
                        color={result.isValid ? 'success' : 'error'}
                      />
                    </Box>
                    <Typography variant="h5" className="doc-number">
                      {result.docNumber || 'Not found'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {result.name && (
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" className="result-card">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary">Extracted Name</Typography>
                      <Typography variant="h6">{result.name}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {result.dob && (
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" className="result-card">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary">Extracted DOB</Typography>
                      <Typography variant="h6">{result.dob}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {result.gender && (
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" className="result-card">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary">Extracted Gender</Typography>
                      <Typography variant="h6">{result.gender}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {result.docNumber && (
              <Box className="action-bar" mt={2}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<VerifiedUser />}
                  onClick={handleVerifyWithDecentro}
                  disabled={verifying}
                >
                  {verifying ? <CircularProgress size={20} color="inherit" /> : 'Verify via Decentro'}
                </Button>
              </Box>
            )}

            {decentroResult && (
              <Alert severity={decentroResult.success ? 'success' : 'error'} className="decentro-alert">
                {decentroResult.success
                  ? 'Document verified successfully with external provider'
                  : decentroResult.error}
              </Alert>
            )}

            <Box mt={2}>
              <Typography variant="subtitle2" color="textSecondary" mb={1}>Raw OCR Text</Typography>
              <Paper variant="outlined" className="raw-text">
                <pre>{result.rawText}</pre>
              </Paper>
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default KycDocumentVerification;
